import axios from 'axios';
import { axiosClient } from '@/libs/axios-client';

// Always go through the server's /oz proxy: it authenticates the caller and
// adds the shared secret the engine requires. Talking to the engine directly
// (previously the local default, port 4555) bypasses both.
const OZ_API_URL = import.meta.env.VITE_OZ_API_URL || 'http://localhost:4444/oz';

const ozClient = axios.create({
  baseURL: OZ_API_URL,
  headers: { 'Content-Type': 'application/json' },
  // The proxy authenticates via the session cookie.
  withCredentials: true,
});

// Config
export const getCompanyConfig = async (companyId) => {
  const { data } = await ozClient.get(`/config/company/${companyId}`);
  return data;
};

export const updateCompanyConfig = async (companyId, config) => {
  const { data } = await ozClient.put(`/config/company/${companyId}`, config);
  return data;
};

export const getEmployeeProfiles = async (companyId) => {
  const { data } = await ozClient.get(`/config/employees/${companyId}`);
  return data;
};

export const updateEmployeeProfile = async (employeeId, profile) => {
  const { data } = await ozClient.put(
    `/config/employee/${employeeId}`,
    profile
  );
  return data;
};

export const bulkUpdateProfiles = async (payload) => {
  const { data } = await ozClient.put('/config/employees/bulk-update', payload);
  return data;
};

// AB Soft import (IA1 / IA31 / IA43 / IA42)
export const importAbsoftCsv = async ({
  companyId,
  kind,
  file,
  preview,
  params = {},
}) => {
  const formData = new FormData();
  formData.append('file', file);
  const qs = new URLSearchParams({
    ...(preview ? { preview: 'true' } : {}),
    ...params,
  }).toString();
  const { data } = await ozClient.post(
    `/import/${kind}/${companyId}${qs ? `?${qs}` : ''}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
};

// Payroll
export const runPayroll = async (payload) => {
  const { data } = await ozClient.post('/payroll/run', payload);
  return data;
};

export const dryRunPayroll = async (payload) => {
  const { data } = await ozClient.post('/payroll/dry-run', payload);
  return data;
};

// One-off other-income payout (service contract / author / supplementary / PP).
// payload: { companyId, employeeId, month, year, amount, amountIsNet, otherIncome? }
export const oneOffOtherIncome = async (payload) => {
  const { data } = await ozClient.post(
    '/payroll/other-income/oneoff',
    payload
  );
  return data;
};

export const getPayrollRun = async (runId) => {
  const { data } = await ozClient.get(`/payroll/run/${runId}`);
  return data;
};

export const getPayrollRuns = async (companyId, limit = 20, offset = 0) => {
  const { data } = await ozClient.get('/payroll/runs', {
    params: { companyId, limit, offset },
  });
  return data;
};

export const deletePayrollRun = async (runId) => {
  const { data } = await ozClient.delete(`/payroll/run/${runId}`);
  return data;
};

export const downloadPayrollXlsx = async (runId) => {
  const response = await ozClient.get(
    `/payroll/run/${runId}/export-xlsx`,
    { responseType: 'blob' }
  );
  const contentDisposition =
    response.headers['content-disposition'] || '';
  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  const filename = match ? match[1] : `HLB-Obracun-${runId}.xlsx`;

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Payslips in the AB Soft layout. Served by server-hlb, not the engine — the
 * Handlebars template and the Puppeteer pool live there and are shared with the
 * existing salary-slip translation.
 */
export const downloadOzPayslips = async (runId, language = 'sr') => {
  const { data } = await axiosClient.get(`/salaries/oz-payslips/${runId}`, {
    params: { language },
    responseType: 'blob',
  });

  const url = window.URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `obracunski-listici-${runId}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Shadow
export const compareShadow = async (payload) => {
  const { data } = await ozClient.post('/shadow/compare', payload);
  return data;
};

export const uploadGroundTruth = async ({ runId, file, tolerance }) => {
  const formData = new FormData();
  formData.append('file', file);
  if (tolerance != null) formData.append('tolerance', String(tolerance));
  const { data } = await ozClient.post(
    `/shadow/upload-ground-truth/${runId}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
};

export const getShadowReport = async (runId) => {
  const { data } = await ozClient.get(`/shadow/report/${runId}`);
  return data;
};

// Employee status periods — per-emp timeline of (010/020/030/040/050/060)
export const listEmployeeStatusPeriods = async (employeeId) => {
  const { data } = await ozClient.get(`/employee-status/employee/${employeeId}`);
  return data;
};

export const listCompanyStatusPeriods = async (companyId) => {
  const { data } = await ozClient.get(`/employee-status/company/${companyId}`);
  return data;
};

export const createEmployeeStatusPeriod = async (payload) => {
  const { data } = await ozClient.post('/employee-status', payload);
  return data;
};

export const updateEmployeeStatusPeriod = async (id, payload) => {
  const { data } = await ozClient.put(`/employee-status/${id}`, payload);
  return data;
};

export const deleteEmployeeStatusPeriod = async (id) => {
  const { data } = await ozClient.delete(`/employee-status/${id}`);
  return data;
};

// RS Rules — period-versioned region data (techAdmin only)
export const listRsRuleTypes = async () => {
  const { data } = await ozClient.get('/rs-rules');
  return data;
};

export const listRsRules = async (type) => {
  const { data } = await ozClient.get(`/rs-rules/${type}`);
  return data;
};

export const createRsRule = async (type, payload) => {
  const { data } = await ozClient.post(`/rs-rules/${type}`, payload);
  return data;
};

export const updateRsRule = async (type, id, payload) => {
  const { data } = await ozClient.put(`/rs-rules/${type}/${id}`, payload);
  return data;
};

export const deleteRsRule = async (type, id) => {
  const { data } = await ozClient.delete(`/rs-rules/${type}/${id}`);
  return data;
};

// Earning-code usage across firm configs: { usage: { [code]: [companyName] } }.
// Feeds the "firme" coverage column in RsRulesPanel earning-codes tab.
export const getEarningCodeUsage = async () => {
  const { data } = await ozClient.get('/diagnostics/earning-code-usage');
  return data;
};

// Diagnostics (read-only) — data the engine consults but has no edit UI.
export const getHistoricalEarnings = async (companyId, params = {}) => {
  const { data } = await ozClient.get(
    `/diagnostics/historical-earnings/${companyId}`,
    { params }
  );
  return data;
};

export const getAbsoftTargets = async (companyId, params = {}) => {
  const { data } = await ozClient.get(
    `/diagnostics/absoft-targets/${companyId}`,
    { params }
  );
  return data;
};
